import { ChevronDown, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Column } from '@tanstack/react-table';

import { IButtonTableHeader} from './components/ButtonTableHeader';
import { IDropdownTableHeader} from "@/components/new/table/header/components/DropdownTableHeader";
import {useTableContext} from "../TableContext";
import {HeaderElement} from "@/components/new/table/header/components/HeaderElement";
import {ITextTableHeader} from "@/components/new/table/header/components/TextTableHeader";


interface CommonHeaderButtonProps {
    label: string
    icon?: React.ReactNode
    type: string
}
export type HeaderActionButton = (IButtonTableHeader | IDropdownTableHeader | ITextTableHeader) & CommonHeaderButtonProps

interface HeaderActionsProps<TData,TValue> {
    columns: Column<TData, TValue>[]
}

export const HeaderActions = <TData, TValue>({columns}: HeaderActionsProps<TData,TValue>) => {
    const [isMobileView, setIsMobileView] = useState(false)

    // Use the context
    const { paginate, data, isLoading, refetch, rightButtons = [], leftButtons = [], showColumnsDropdown } =
        useTableContext()

    // Check for mobile view on mount and window resize
    useEffect(() => {
        const checkViewport = () => {
            setIsMobileView(window.innerWidth < 768)
        }

        checkViewport()
        window.addEventListener('resize', checkViewport)

        return () => {
            window.removeEventListener('resize', checkViewport)
        }
    }, [])


    // For mobile: stacked layout
    if (isMobileView) {
        return (
            <div className="flex-col gap-4 p-4">
                <div className="mb-4 flex w-full items-center justify-between">
                    {refetch &&
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={refetch}
                                disabled={isLoading}
                                aria-label="Refrescar tabla"
                            >
                                {isLoading ? (
                                    <div
                                        className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"/>
                                ) : (
                                    <RefreshCw className="h-4 w-4"/>
                                )}
                            </Button>
                        </div>
                    }

                    <div className="flex items-center gap-2">
                        {showColumnsDropdown &&
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-8 gap-1"
                                        aria-label="Mostrar columnas"
                                    >
                                        <span>Columnas</span>
                                        <ChevronDown className="h-3.5 w-3.5"/>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuLabel>Mostrar columnas</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {columns
                                        .filter((column) => column.getCanHide())
                                        .map((column) => {
                                            return (
                                                <DropdownMenuCheckboxItem
                                                    key={column.id}
                                                    checked={column.getIsVisible()}
                                                    onCheckedChange={(value) =>
                                                        column.toggleVisibility(value)
                                                    }
                                                    className="cursor-pointer"
                                                >
                                                    {typeof column.columnDef.header === 'string'
                                                        ? column.columnDef.header
                                                        : column.id}
                                                </DropdownMenuCheckboxItem>
                                            )
                                        })}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        }

                        {rightButtons.map((btn, idx) => {
                            return (
                                <HeaderElement
                                    key={idx}
                                    headerActionButton={btn}
                                />
                            )
                        })
                        }
                    </div>
                </div>

                <div className="flex w-full flex-col gap-2">
                    {leftButtons.map((btn, idx) => {
                        return (
                            <HeaderElement
                                key={idx}
                                headerActionButton={btn}
                            />
                        )
                    })
                    }
                </div>
            </div>
        )
    }



    // For desktop: layout with creation buttons on left, pagination in middle, and utility buttons on right
    return (
        <div className="flex items-center justify-between p-4">
            {/* Left - Invoice creation buttons */}
            <div className="flex items-center gap-2">
                {leftButtons.map((btn, idx) => {
                    return (
                        <HeaderElement
                            key={idx}
                            headerActionButton={btn}
                        />
                    )
                })
                }
            </div>

            {/* Middle - Pagination info */}
            <div className="flex items-center gap-2">
                {refetch &&
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={refetch}
                        disabled={isLoading}
                        aria-label="Refrescar tabla"
                    >
                        {isLoading ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        ) : (
                            <RefreshCw className="h-4 w-4" />
                        )}
                    </Button>
                }
                {paginate &&
                    <span className="min-h-[20px] min-w-[300px] text-sm text-muted-foreground">
                      {isLoading ? (
                          <Skeleton className="h-4 w-[300px]"/>
                      ) : (
                          <>
                              Mostrando {(paginate.currentPage - 1) * paginate.pageSize + 1} al{' '}
                              {Math.min(paginate.currentPage * paginate.pageSize, paginate.totalItems)} de{' '}
                              {paginate.totalItems} resultados
                          </>
                      )}
                    </span>
                }
            </div>

            {/* Right - Export and columns buttons */}
            <div className="flex items-center gap-2">
                {rightButtons.map((btn, idx) => {
                    return (
                        <HeaderElement
                            key={idx}
                            headerActionButton={btn}
                        />
                    )
                })
                }
                {showColumnsDropdown &&
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 gap-1"
                                aria-label="Mostrar columnas"
                            >
                                <span>Columnas</span>
                                <ChevronDown className="h-3.5 w-3.5" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel>Mostrar columnas</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {columns
                                .filter((column) => column.getCanHide())
                                .map((column) => {
                                    return (
                                        <DropdownMenuCheckboxItem
                                            key={column.id}
                                            checked={column.getIsVisible()}
                                            onCheckedChange={(value) =>
                                                column.toggleVisibility(value)
                                            }
                                            className="cursor-pointer"
                                        >
                                            {typeof column.columnDef.header === 'string'
                                                ? column.columnDef.header
                                                : column.id}
                                        </DropdownMenuCheckboxItem>
                                    )
                                })}
                        </DropdownMenuContent>
                    </DropdownMenu>
                }

            </div>

        </div>
    )
}
